/* istanbul ignore file: todo(eran): not running image tests on this file yet */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-base-to-string */
/* eslint-disable sonarjs/cognitive-complexity */

import React, {useRef} from 'react';
import {ImageUiTpaWrapper} from './ImageUiTpaWrapper';
import s from './ProductMedia.scss';
import {HoverType, ImageModeId, ImageRatioId, IProduct, ProductType} from '../../../../types/galleryTypes';
import {ImageResizeOptions} from '@wix/wix-ui-tpa';
import {DigitalProductBadge} from '@wix/wixstores-client-common-components/dist/es/src/icons/dist';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {Carousel, CarouselAriaLabels} from '../../Carousel/Carousel';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import {StoresWidgetID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Experiments} from '../../../../constants';
import {WishlistButton} from '../WishlistButton/WishlistButton';

export interface IProductImageProps extends IGalleryGlobalProps {
  product: IProduct;
  isMobile: boolean;
  hoverType: string;
  imageModeId: number;
  classNames: {thumbnail: string; image: string};
  ribbon?: React.ReactChild;
  textsMap: {
    digitalProductBadgeAriaLabel: string;
  };
}

export enum ProductMediaDataHook {
  Images = 'ProductMediaDataHook.Images',
  ProductMedia = 'ProductMediaDataHook.ProductMedia',
  Badge = 'ProductMediaDataHook.Badge',
  RibbonAndWishlistFlexContainer = 'ProductMediaDataHook.RibbonAndWishlistFlexContainer',
  WishlistAndBadgesContainer = 'ProductMediaDataHook.WishlistAndBadgesContainer',
}

export const MAX_CAROUSEL_IMAGES = 5;
export const getProductMediaDataHook = (key: number) => `${ProductMediaDataHook.ProductMedia}-${key}`;

export const ratioIdToRatioMap = {
  [ImageRatioId._3x2]: 3 / 2,
  [ImageRatioId._4x3]: 4 / 3,
  [ImageRatioId._1x1]: 1,
  [ImageRatioId._3x4]: 3 / 4,
  [ImageRatioId._2x3]: 2 / 3,
  [ImageRatioId._16x9]: 16 / 9,
  [ImageRatioId._9x16]: 9 / 16,
};

const ProductImageComponent: React.FC<React.PropsWithChildren<IProductImageProps & IProvidedTranslationProps>> = (
  props
) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const renderDigitalBadge = () => {
    return (
      <div
        className={s.productBadge}
        data-hook={ProductMediaDataHook.Badge}
        aria-label={props.textsMap.digitalProductBadgeAriaLabel}>
        <DigitalProductBadge />
      </div>
    );
  };

  const imageRatio = () => {
    const {
      globals: {styles, stylesParams, shouldShowMobile},
    } = props;
    return styles.get(stylesParams[shouldShowMobile ? 'mobile:galleryImageRatio' : 'galleryImageRatio']);
  };

  const renderImages = () => {
    const {
      hoverType,
      imageModeId,
      isMobile,
      product,
      classNames: {thumbnail: externalThumbnailClass, image: externalImageClass},
      globals: {shouldShowImageCarousel},
    } = props;

    let showAmountOfPhotos: number = 1;

    const shouldShowSecondaryImageByHoverType = !isMobile && hoverType === HoverType.Alternate;

    if (product.media.length >= 2) {
      if (shouldShowImageCarousel) {
        showAmountOfPhotos = Math.min(product.media.length, MAX_CAROUSEL_IMAGES);
      } else if (shouldShowSecondaryImageByHoverType) {
        showAmountOfPhotos = 2;
      }
    }

    return Array.from({length: showAmountOfPhotos}, (_value, key) => (
      <ImageUiTpaWrapper
        hoverType={hoverType as HoverType}
        mediaItemIndex={key}
        key={key}
        globals={props.globals}
        wrapperClassName={externalThumbnailClass}
        imageClassName={externalImageClass}
        product={product}
        data-hook={getProductMediaDataHook(key)}
        resize={imageModeId === ImageModeId.Crop ? ImageResizeOptions.cover : ImageResizeOptions.contain}
        containerAspectRatio={ratioIdToRatioMap[imageRatio()]}
      />
    ));
  };

  const renderWishlistAndBadgesContainer = () => {
    const {product} = props;
    const isDigitalProduct = product.productType === ProductType.DIGITAL;

    return (
      <div data-hook={ProductMediaDataHook.RibbonAndWishlistFlexContainer} className={s.ribbonAndWishlistFlexContainer}>
        {props.ribbon}
        {isDigitalProduct ? (
          <ConditionalRender
            by={'isWishlistAndDigitalBadgeEnabled'}
            className={classNames(s.wishlistAndBadgesContainer)}
            dataHook={ProductMediaDataHook.WishlistAndBadgesContainer}>
            {shouldShowWishlistButton && (
              <ConditionalRender by={'gallery_wishlistEnabled'}>
                {<WishlistButton productId={product.id} />}
              </ConditionalRender>
            )}
            <ConditionalRender by={'gallery_showDigitalBadge'}>{renderDigitalBadge()}</ConditionalRender>
          </ConditionalRender>
        ) : (
          shouldShowWishlistButton && (
            <ConditionalRender
              by={'gallery_wishlistEnabled'}
              className={classNames(s.wishlistAndBadgesContainer)}
              dataHook={ProductMediaDataHook.WishlistAndBadgesContainer}>
              <WishlistButton productId={product.id} />
            </ConditionalRender>
          )
        )}
      </div>
    );
  };

  const {
    product,
    globals: {shouldShowImageCarousel, isLiveSiteMode, isPreviewMode, isEditorMode, storesWidgetId},
    t,
  } = props;
  const {experiments} = useExperiments();
  const isShowWishlistInGalleryExperimentEnabled = experiments.enabled(Experiments.ShowWishlistInGallery);
  const isFixWishlistControllerConfigTypeEnabled = experiments.enabled(Experiments.FixWishlistControllerConfigType);
  const isWishlistPage = storesWidgetId === StoresWidgetID.WISHLIST_PAGE;
  const shouldShowWishlistButton =
    isShowWishlistInGalleryExperimentEnabled && (!isFixWishlistControllerConfigTypeEnabled || !isWishlistPage);

  const ariaLabels: CarouselAriaLabels = {
    arrowLeft: t('arrowPrevious'),
    arrowRight: t('arrowNext'),
  };

  const imageAltTexts = product.media.slice(0, MAX_CAROUSEL_IMAGES).map((media) => media?.altText || '');

  const shouldWrapWithImageCarousel = shouldShowImageCarousel && (product.media.length > 1 || isEditorMode);

  const renderedImages = renderImages();

  const renderProductMediaWithOverlayContainer = (overlayContent: React.ReactNode) => (
    <div className={s.productMediaContainer}>
      <div
        className={classNames(
          s.productImages,
          s.withBorderRadius,
          'heightByImageRatio',
          `heightByImageRatio${imageRatio()}`
        )}
        ref={imageContainerRef}
        aria-live={'assertive'}
        data-hook={ProductMediaDataHook.Images}>
        {shouldWrapWithImageCarousel ? (
          <Carousel
            ariaLabels={ariaLabels}
            imageAltTexts={imageAltTexts}
            shouldShowArrowsOnHover={isLiveSiteMode || isPreviewMode}
            imageCount={renderedImages.length}>
            {renderedImages}
          </Carousel>
        ) : (
          renderedImages
        )}
        {!isShowWishlistInGalleryExperimentEnabled && product.productType === ProductType.DIGITAL && (
          <ConditionalRender by={'gallery_showDigitalBadge'}>{renderDigitalBadge()}</ConditionalRender>
        )}
        {props.children}
      </div>
      {overlayContent}
    </div>
  );

  const overlayContent = isShowWishlistInGalleryExperimentEnabled ? renderWishlistAndBadgesContainer() : props.ribbon;
  return renderProductMediaWithOverlayContainer(overlayContent);
};

export const ProductMedia = withTranslations()(ProductImageComponent);
